import React from 'react';
import { Box, Typography, Skeleton, useTheme } from '@mui/material';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { PnlHistogramBin, HoldTimeBucket } from '../../hooks/useStatisticsPanels';
import { accentColors, pnlColors } from '../../design/theme';

const CHART_HEIGHT = 260;
const STRADDLE_FILL = accentColors.binNeutral;

const OffscreenDescription: React.FC<{ label: string }> = ({ label }) => (
  <Box
    role="img"
    aria-label={label}
    sx={{ position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
  >
    {label}
  </Box>
);

const EmptyState: React.FC = () => (
  <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant="body2" color="text.secondary">
      No realized trades in this period
    </Typography>
  </Box>
);

// --- P&L histogram ---

interface PnlHistogramProps {
  data: PnlHistogramBin[] | null;
  loading: boolean;
}

const binFill = (bin: PnlHistogramBin): string => {
  if (bin.bin_end <= 0) return pnlColors.loss;
  if (bin.bin_start >= 0) return pnlColors.gain;
  return STRADDLE_FILL;
};

const binLabel = (bin: PnlHistogramBin): string =>
  `$${Math.round(bin.bin_start)}…$${Math.round(bin.bin_end)}`;

const PnlTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const bin = payload[0].payload as PnlHistogramBin & { label: string };
  return (
    <Box sx={{ backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{bin.label}</Typography>
      <Typography variant="caption" color="text.secondary">{bin.count} trades</Typography>
    </Box>
  );
};

export const PnlHistogram: React.FC<PnlHistogramProps> = ({ data, loading }) => {
  if (loading) return <Skeleton variant="rounded" height={CHART_HEIGHT} />;
  if (!data || data.length === 0) return <EmptyState />;

  const rows = data.map((bin) => ({ ...bin, label: binLabel(bin) }));
  const description = `Histogram of realized profit and loss across ${rows.length} bins ranging from ${binLabel(data[0])} to ${binLabel(data[data.length - 1])}.`;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: CHART_HEIGHT }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tickLine={false} fontSize={10} interval="preserveStartEnd" />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} width={32} />
          <Tooltip content={<PnlTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          <Bar dataKey="count">
            {rows.map((bin, i) => (
              <Cell key={i} fill={binFill(bin)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <OffscreenDescription label={description} />
    </Box>
  );
};

// --- Hold time ---

interface HoldTimeChartProps {
  data: HoldTimeBucket[] | null;
  loading: boolean;
}

const HoldTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const bucket = payload[0].payload as HoldTimeBucket;
  return (
    <Box sx={{ backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{bucket.bucket}</Typography>
      <Typography variant="caption" color="text.secondary">{bucket.count} trades</Typography>
    </Box>
  );
};

export const HoldTimeChart: React.FC<HoldTimeChartProps> = ({ data, loading }) => {
  const theme = useTheme();
  if (loading) return <Skeleton variant="rounded" height={CHART_HEIGHT} />;
  if (!data || data.length === 0) return <EmptyState />;

  const description = `Distribution of trade hold times across ${data.length} buckets.`;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: CHART_HEIGHT }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="bucket" tickLine={false} fontSize={10} interval="preserveStartEnd" />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} width={32} />
          <Tooltip content={<HoldTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          <Bar dataKey="count" fill={theme.palette.primary.main} />
        </BarChart>
      </ResponsiveContainer>
      <OffscreenDescription label={description} />
    </Box>
  );
};
